import {ElementDto} from "../../../../../common/apis/element/ElementDto";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import {_transl} from "../../../../../store/localization/TranslMessasge";
import {CommonTranslation} from "../../CommonTranslation";
import {useState} from "react";
import {formatElement} from "../../elements/ElementFormatter";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import ElementsPickDialog from "../../elements/ElementsPickDialog";
import AlertDialog, {AlertDialogType} from "../../../../../components/dialogs/AlertDialog";
import {ArchimateRelationship} from "../../../../../common/archimate/ArchimateRelationship";
import {TextFieldWithMoreIcon} from "../../../../../components/TextFieldWithAdornmentIcon";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import {SaveButton} from "../../../../../components/button/SaveButton";
import {CancelButton} from "../../../../../components/button/CancelButton";
import Grid from "../../../../../components/dialogs/Grid";
import elementsService from "../../elements/service/ElementService";
import {RelationshipsTranslationKey} from "../RelationshipsTranslationKey";

enum Direction {
    OUTGOING = "outgoing",
    INCOMING = "incoming"
}

interface RelationshipsCreateDialogProps {
    originalElement: ElementDto;
    onSave: () => void,
    onClosed: () => void,
}

export function RelationshipCreateDialog(props: RelationshipsCreateDialogProps) {
    const {originalElement, onSave, onClosed} = props;

    const [isUnsuccessfullyValidated, setUnsuccessfullyValidated] = useState<boolean>(false);
    const [errorDialogOpened, setErrorDialogOpened] = useState(false);
    const [pickDialogOpened, setPickDialogOpened] = useState(false);
    const [pickedElement, setPickedElement] = useState<ElementDto | undefined>(undefined);
    const [direction, setDirection] = useState<string | null>(null);
    const [type, setType] = useState<ArchimateRelationship | null>(null);
    const [directionError, setDirectionError] = useState<string | undefined>(undefined);
    const [typeError, setTypeError] = useState<string | undefined>(undefined);
    const [pickedElementError, setPickedElementError] = useState<string | undefined>(undefined);
    const formattedCommonElementName = formatElement(pickedElement);

    function onElementsPicked(elements: ElementDto[]) {
        const element = elements[0];
        if (!element.acl.canUpdate && !originalElement.acl.canUpdate) {
            setErrorDialogOpened(true)
        } else {
            isUnsuccessfullyValidated && validatePickedElement(element);
            setPickedElement(element)
            setPickDialogOpened(false);
        }
    }

    const validateDirection = (direction: string | null): boolean => {
        if (direction === null) {
            setDirectionError(_transl(CommonTranslation.FILL_OUT_ITEM_FROM_LIST));
            return false;
        } else {
            setDirectionError(undefined);
            return true;
        }
    };

    const validateType = (type: ArchimateRelationship | null): boolean => {
        if (type === undefined) {
            setTypeError(_transl(CommonTranslation.FILL_OUT_ITEM_FROM_LIST));
            return false;
        } else {
            setTypeError(undefined);
            return true;
        }
    };

    const validatePickedElement = (pickedElement: ElementDto | undefined): boolean => {
        if (pickedElement === undefined) {
            setPickedElementError(_transl(CommonTranslation.FILL_OUT_ITEM_FROM_LIST));
            return false;
        } else {
            setPickedElementError(undefined);
            return true;
        }
    };

    const isFormValid = (): boolean => {
        const isDirectionValid = validateDirection(direction);
        const isTypeValid = validateType(type);
        const isPickedElementValid = validatePickedElement(pickedElement);
        return isDirectionValid && isTypeValid && isPickedElementValid;
    };

    function saveChanges() {
        if (isFormValid()) {
            const identifier = pickedElement!.identifier;
            const relationshipDirection = direction === Direction.OUTGOING;

            if (identifier !== undefined) {
                createRelationship(identifier, relationshipDirection);
            }
        } else {
            setUnsuccessfullyValidated(true)
        }
    }

    function createRelationship(identifier: string, relationshipDirection: boolean) {
        elementsService.createRelationship(identifier, [originalElement.identifier], type!.standardNames[0], relationshipDirection)
            .then(() => {
                onSave();
                onClosed();
                Snackbar.success(_transl(CommonTranslation.DATA_SAVED_SUCESSFULLY));
            })
            .catch(() => {
                Snackbar.error(_transl(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED));
            });
    }

    function getLabelForDirection(direction: Direction) {
        return (direction === Direction.OUTGOING)
                ? _transl(RelationshipsTranslationKey.PICKED_ELEMENT_IS_SOURCE)
                : _transl(RelationshipsTranslationKey.PICKED_ELEMENT_IS_TARGET);
    }

    return (
        <>
            <Dialog open maxWidth={"md"} onClose={onClosed}>
                <DialogTitle id="scroll-dialog-title" title={_transl(RelationshipsTranslationKey.NEW_RELATIONSHIP)} onDialogClosed={onClosed}/>
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12}>
                            <SingleSelectField
                                    id={"direction-field"}
                                    label={_transl(RelationshipsTranslationKey.RELATIONSHIP_DIRECTION)}
                                    value={direction}
                                    onChange={(value) => {
                                        isUnsuccessfullyValidated && validateDirection(value);
                                        setDirection(value ?? null);
                                    }}
                                    options={[Direction.OUTGOING, Direction.INCOMING]}
                                    getOptionLabel={(value) => getLabelForDirection(value as Direction)}
                                    required={true}
                                    errorMessage={directionError}/>
                        </Grid>
                        <Grid item xs={12}>
                            <SingleSelectField
                                    id={"type-field"}
                                    label={_transl(RelationshipsTranslationKey.RELATIONSHIP_TYPE)}
                                    value={type}
                                    onChange={(value) => {
                                        isUnsuccessfullyValidated && validateType(value ?? null);
                                        setType(value);
                                    }}
                                    options={ArchimateRelationship.values()}
                                    getOptionLabel={option => option.visibleName}
                                    errorMessage={typeError}
                                    required={true}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldWithMoreIcon key={formattedCommonElementName}
                                                   id="assignedItem-field"
                                                   required={true}
                                                   value={formattedCommonElementName}
                                                   label={_transl(RelationshipsTranslationKey.PICKED_ELEMENT)}
                                                   errorMessage={pickedElementError}
                                                   tooltipTitle={_transl(RelationshipsTranslationKey.FIND_ELEMENT)}
                                                   onClick={() => setPickDialogOpened(true)}/>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <SaveButton onClick={saveChanges}/>
                    <CancelButton onClick={onClosed}/>
                </DialogActions>
            </Dialog>
            <ElementsPickDialog isOpened={pickDialogOpened}
                                isMultiSelection={false}
                                onElementsPicked={onElementsPicked}
                                onDialogClosed={() => setPickDialogOpened(false)}/>

            <AlertDialog open={errorDialogOpened}
                         title={_transl(RelationshipsTranslationKey.INSUFFICIENT_PERMISSIONS)}
                         text={_transl(RelationshipsTranslationKey.INSUFFICIENT_PERMISSIONS_TO_CREATE_RELATIONSHIP_BETWEEN_ELEMENTS)}
                         type={AlertDialogType.WARNING}
                         onClose={() => setErrorDialogOpened(false)}/>
        </>
    );
}
